<template>
  <teleport to="#teleports">
    <div ref="modalWrapper" class="absolute" tabindex="-1">
      <transition name="fade">
        <div v-if="show"
             data-testid="bf-modal-backdrop"
             :class="[{'bg-paper-black bg-opacity-40': modalBackDrop}]"
             :style="zHeightMinusTen"
             class="dark:bg-opacity-80 fixed inset-0"
             @mousedown="clickOutside"/>
      </transition>

      <div :style="zHeight"
           data-testid="bf-modal-layer"
           data-bf-overlay-layer="modal"
           :data-bf-overlay-z-index="modalZIndex"
           class="pointer-events-none flex overflow-y-auto overflow-x-hidden fixed top-0 right-0 left-0 justify-center w-full md:inset-0 h-screen max-h-screen"
           tabindex="-1">
        <transition name="fade-from-top"
                    @after-enter="activateFocusTrap"
                    @after-leave="deactivateFocusTrap">
          <div v-if="show"
               :class="[sizeClasses, placementClasses]"
               class="inline-flex relative h-full w-full pointer-events-none">
            <!-- Modal content -->
            <div ref="modalElement"
                 data-testid="bf-modal-shell"
                 :class="[{'h-full' : fullheight}, computedModalClass]"
                 class="flex flex-col relative w-full pointer-events-auto cursor-auto bg-paper-white border border-paper-border rounded-lg paper-shadow overflow-hidden min-h-[280px] min-w-[345px] max-h-[calc(100vh-60px)]"
                 tabindex="-1"
                 v-bind="forwardedAttrs">
              <span ref="focusTrapFallback"
                    aria-hidden="true"
                    class="sr-only"
                    tabindex="-1"/>
              <!-- Modal header -->
              <div v-if="$slots.header"
                   class="flex items-center justify-between p-6 text-4xl font-bold">
                <slot name="header"/>
              </div>
              <!-- Modal body -->
              <div v-if="$slots.body"
                   :class="[bodyClass, {
                     'flex-grow': !$slots.scrollBody,
                     'shrink-0': $slots.scrollBody,
                     'overflow-hidden': fullheight,
                     'px-6': !fullheight && padding,
                     'overflow-y-auto': !fullheight && !$slots.scrollBody,
                     'pt-6': !$slots.header && padding,
                     'pb-6': !$slots.scrollBody && padding
                   }]"
                   class="relative min-h-0"
                   data-testid="bf-modal-body">
                <slot name="body"/>
              </div>
              <!-- Scrollable overflow body -->
              <div v-if="$slots.scrollBody"
                   data-testid="bf-modal-scroll-body"
                   :class="[scrollBodyClass, {'min-h-[25rem]' : !$slots.bottomBody && !$slots.body}]"
                   class="relative min-h-0 flex-1 overflow-y-auto overflow-x-hidden mx-3 px-3 mt-2">
                <slot name="scrollBody"/>
              </div>
              <!-- bottom body -->
              <div v-if="$slots.bottomBody"
                   :class="{'overflow-hidden' : fullheight, 'px-6': !fullheight, 'pt-6': !$slots.scrollBody}"
                   class="relative flex-grow">
                <slot name="bottomBody"/>
              </div>
              <!-- Modal footer -->
              <div v-if="$slots.footer"
                   class="mt-auto overflow-hidden p-6 dark:border-gray-600 bg-white shrink-0"
                   data-testid="bf-modal-footer">
                <slot name="footer"/>
              </div>
            </div>
          </div>
        </transition>
      </div>
    </div>
  </teleport>
</template>

<script lang="ts" setup>
import type {PropType} from 'vue';
import type {ModalPlacement, ModalSize} from '~/components/global/utils/modalUtils';
import {useFocusTrap, type UseFocusTrapOptions} from '@vueuse/integrations/useFocusTrap';
import {
  BF_MODAL_DEFAULT_Z_INDEX,
  BF_OVERLAY_LAYER_ATTRIBUTE,
  BF_OVERLAY_Z_INDEX_ATTRIBUTE,
  getBfOverlayZIndex
} from '~/utils/bfOverlayZIndex';

const $emit = defineEmits(['close', 'click:outside']);
defineOptions({
  inheritAttrs: false
});
const attrs = useAttrs();
const bodyClass = computed(() => attrs['body-class']);
const scrollBodyClass = computed(() => attrs['scroll-body-class']);
const forwardedAttrs = computed(() =>
  Object.fromEntries(
    Object.entries(attrs).filter(([k]) =>
      !['body-class', 'scroll-body-class'].includes(k)
    )
  )
);
const props = defineProps({
  show: {
    type: Boolean,
    required: true
  },

  size: {
    type: String as PropType<ModalSize>,
    default: 'md'
  },

  placement: {
    type: String as PropType<ModalPlacement>,
    default: 'top'
  },

  persistent: Boolean,

  escapable: {
    type: Boolean,
    default: false
  },

  fullheight: {
    type: Boolean,
    default: false
  },

  zIndex: {
    type: Number,
    default: BF_MODAL_DEFAULT_Z_INDEX,
    required: false
  },

  padding: {
    type: Boolean,
    required: false,
    default: true
  },
  modalClass: {
    type: String,
    required: false,
    default: ''
  },
  modalBackDrop: {
    type: Boolean,
    required: false,
    default: true
  }

});

const isVisible = (el: HTMLElement): boolean => {
  const style = window.getComputedStyle(el);
  return style.display !== 'none' && style.visibility !== 'hidden';
};

const modalWrapper = ref<HTMLElement | null>(null);
const modalElement = ref<HTMLElement | null>(null);
const focusTrapFallback = ref<HTMLElement | null>(null);
const modalIsActive = ref(false);
const {backdropZIndex, hasOpenModals, modalZIndex, registerModal, unregisterModal} = useBfModalStack(() => props.zIndex);
const floatingLayerElements = ref<HTMLElement[]>([]);
let mutationObserver: {
  isSupported: ComputedRef<boolean>
  stop: () => void
  takeRecords: () => (MutationRecord[] | undefined)
} | null = null;

const isFloatingLayerAboveCurrentModal = (element: HTMLElement): boolean => {
  if (!isVisible(element)) {
    return false;
  }

  return (getBfOverlayZIndex(element) ?? 0) > modalZIndex.value;
};

const updateFloatingLayerElements = () => {
  floatingLayerElements.value = Array.from(
    document.querySelectorAll(`[${BF_OVERLAY_LAYER_ATTRIBUTE}="floating"]`)
  ).filter((el): el is HTMLElement => el instanceof HTMLElement && isFloatingLayerAboveCurrentModal(el));
};

watch(floatingLayerElements, (newElements) => {
  if (newElements.length > 0) {
    pause();
  } else {
    unpause();
  }
});

const computedModalClass = computed(() => {
  return props.modalClass;
});

const getFocusTrapFallbackTarget = (): HTMLElement | null => {
  if (focusTrapFallback.value instanceof HTMLElement) {
    return focusTrapFallback.value;
  }

  if (modalElement.value instanceof HTMLElement) {
    return modalElement.value;
  }

  if (modalWrapper.value instanceof HTMLElement) {
    return modalWrapper.value;
  }

  if (import.meta.client && document.body instanceof HTMLElement) {
    return document.body;
  }

  return null;
};

const getFocusTrapInitialTarget = (): HTMLElement | undefined => {
  if (!(modalElement.value instanceof HTMLElement)) {
    return undefined;
  }

  const autofocusCandidates = modalElement.value.querySelectorAll<HTMLElement>(
    'input[autofocus], textarea[autofocus], select[autofocus]'
  );

  return Array.from(autofocusCandidates).find((element) => {
    return isVisible(element)
      && !element.matches(':disabled')
      && element.getAttribute('aria-hidden') !== 'true';
  });
};

const focusTrapOptions: UseFocusTrapOptions = {
  allowOutsideClick: (event: MouseEvent | TouchEvent): boolean => {
    if (event.target instanceof Element) {
      const floatingLayer = event.target.closest(`[${BF_OVERLAY_LAYER_ATTRIBUTE}="floating"]`);
      return floatingLayer instanceof HTMLElement && isFloatingLayerAboveCurrentModal(floatingLayer);
    }

    return false;
  },
  initialFocus: () => getFocusTrapInitialTarget(),
  fallbackFocus: () => getFocusTrapFallbackTarget()
};
const {activate, deactivate, pause, unpause} = useFocusTrap(modalElement, focusTrapOptions);

const activateFocusTrap = async () => {
  await nextTick();

  if (!props.show || !modalElement.value?.isConnected) {
    return;
  }

  try {
    activate();
  } catch (error) {
    const fallbackTarget = getFocusTrapFallbackTarget();

    fallbackTarget?.focus({preventScroll: true});
    console.warn('Failed to activate modal focus trap', error);
  }
};

const deactivateFocusTrap = () => {
  deactivate();
};

const close = () => {
  $emit('close');
};

const sizeClasses = computed(() => {
  return {
    'xs': 'max-w-xs',
    'sm': 'max-w-sm',
    'md': 'max-w-md',
    'lg': 'max-w-lg',
    'xl': 'max-w-xl',
    '2xl': 'max-w-2xl',
    '3xl': 'max-w-[735px] min-w-[735px]',
    '4xl': 'max-w-4xl',
    '5xl': 'max-w-5xl',
    '6xl': 'max-w-6xl',
    '7xl': 'max-w-7xl',
    '8xl': 'max-w-[100rem]'
  }[props.size];
});
const zHeight = computed(() => {
  return {
    zIndex: modalZIndex.value
  };
});
const zHeightMinusTen = computed(() => {
  return {
    zIndex: backdropZIndex.value
  };
});
const placementClasses = computed(() => {
  return {
    'top': 'items-start',
    'center': 'items-center',
    'bottom': 'items-end'
  }[props.placement];
});

const clickOutside = () => {
  if (!modalIsActive.value) return;
  $emit('click:outside');

  if (!props.persistent)
    close();
};
const handleModalShown = () => {
  if (modalIsActive.value) return;

  modalIsActive.value = true;
  registerModal();

  try {
    if (import.meta.client && document) {
      document.body.classList.add('overflow-hidden'); // Todo: implement BodyScrollLock instead "https://github.com/willmcpo/body-scroll-lock"

      updateFloatingLayerElements();

      mutationObserver = useMutationObserver(document.body, () => {
        updateFloatingLayerElements();
      }, {
        attributeFilter: ['class', 'style', BF_OVERLAY_LAYER_ATTRIBUTE, BF_OVERLAY_Z_INDEX_ATTRIBUTE],
        attributes: true,
        childList: true,
        subtree: true
      });
    }

  } catch (e) {
    console.error(e);
  }
};
const handleModalHidden = () => {
  if (!modalIsActive.value) return;

  modalIsActive.value = false;
  deactivateFocusTrap();
  unregisterModal();

  if (import.meta.client && !hasOpenModals.value)
    document.body.classList.remove('overflow-hidden');

  // Disconnect the mutation observer if it exists.
  if (mutationObserver) {
    mutationObserver.stop();
    mutationObserver = null;
  }

  floatingLayerElements.value = [];
};

onBeforeUnmount(() => {
  handleModalHidden();
});

watch(() => props.show, (newValue) => {
  if (newValue) {
    handleModalShown();
  } else {
    handleModalHidden();
  }
}, {immediate: true});

watch(modalZIndex, () => {
  if (!modalIsActive.value || !import.meta.client) return;

  updateFloatingLayerElements();
});
</script>